import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import {
  Alert,
  Badge,
  Button,
  Label,
  Select,
  TextInput,
  Toggle,
} from '@/components/ui/daisy';
import Exam, { ExamTypeEnum } from '@/models/Exam';
import ExamArea from '@/models/ExamArea';
import { Nullable } from '@/models/Model';

export type ExamBlockFormFields = {
  name: string;
  examAreaId: number;
  hasTime: boolean;
  timeLimit: Nullable<number>;
  shuffleQuestions: boolean;
};

type ExamBlockInlineFormProps = {
  exam: Exam;
  examAreas: ExamArea[];
  defaultValues?: Partial<ExamBlockFormFields>;
  onSubmit: (data: ExamBlockFormFields) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function ExamBlockInlineForm({
  exam,
  examAreas,
  defaultValues,
  onSubmit,
  onCancel,
  isLoading,
}: ExamBlockInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });

  const isSimulated = exam.type === ExamTypeEnum.SIMULATED;
  const maxTime = exam.defaultTime;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ExamBlockFormFields>({
    defaultValues: {
      name: '',
      hasTime: false,
      timeLimit: null,
      shuffleQuestions: false,
      examAreaId: 0,
      ...defaultValues,
    },
  });

  const hasTime = watch('hasTime');

  const submit = (data: ExamBlockFormFields) =>
    onSubmit({
      ...data,
      timeLimit: isSimulated && data.hasTime ? data.timeLimit : null,
    });

  return (
    <FieldsetContainer>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(submit)}>
        <TextInput.Fieldset
          label={t('blockName')}
          labelAlt={errors?.name?.message}
        >
          <TextInput
            type="text"
            placeholder={t('blockNamePlaceholder')}
            className="w-full"
            color={!errors?.name?.message ? 'default' : 'error'}
            {...register('name', { required: t('blockNameRequired') })}
          />
        </TextInput.Fieldset>

        <Select.Fieldset
          label={t('area')}
          labelAlt={errors?.examAreaId?.message}
        >
          <Select
            className="w-full"
            disabled={!!defaultValues}
            color={!errors?.examAreaId?.message ? 'default' : 'error'}
            {...register('examAreaId', {
              valueAsNumber: true,
              validate: value =>
                (Number.isFinite(value) && value > 0) || t('areaRequired'),
            })}
          >
            <Select.Option value="0" disabled>
              {t('areaPlaceholder')}
            </Select.Option>
            {examAreas.map(ea => (
              <Select.Option key={ea.id} value={ea.id}>
                {ea.area.name}
              </Select.Option>
            ))}
          </Select>
        </Select.Fieldset>

        <ConditionalRenderer condition={isSimulated}>
          <div className="flex gap-4 items-end">
            <Label className="text-base-content w-36 flex justify-between items-center">
              {t('hasTime')}
              <Toggle color="primary" {...register('hasTime')} />
            </Label>
            <TextInput.Fieldset
              className="flex-1"
              label={t('timeLimitLabel')}
              labelAlt={errors?.timeLimit?.message}
            >
              <TextInput.Wrapper
                color={!errors?.timeLimit?.message ? 'default' : 'error'}
                className="w-full"
              >
                <TextInput
                  type="number"
                  placeholder={t('timeLimitPlaceholder')}
                  step={1}
                  disabled={!hasTime}
                  {...register('timeLimit', {
                    valueAsNumber: true,
                    validate: value => {
                      if (!hasTime) return true;
                      if (!Number.isFinite(value) || (value ?? 0) <= 0) {
                        return t('timeLimitRequired');
                      }
                      if ((value ?? 0) > maxTime) {
                        return t('timeLimitMaxError', { count: maxTime });
                      }
                      return true;
                    },
                  })}
                />
                <TextInput.Addon>
                  <Badge size="xs" variant="soft">
                    {t('timeLimitMax', { count: maxTime })}
                  </Badge>
                </TextInput.Addon>
              </TextInput.Wrapper>
            </TextInput.Fieldset>
          </div>
        </ConditionalRenderer>

        <div className="flex items-end gap-4">
          <Label className="text-base-content w-36 flex justify-between items-center">
            {t('randomize')}
            <Toggle color="primary" {...register('shuffleQuestions')} />
          </Label>
          <Alert variant="soft" className="flex-1 rounded-2xl">
            <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
            <span className="text-xs">{t('randomizeInfo')}</span>
          </Alert>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="grow bg-base-300"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            className="grow"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FieldsetContainer>
  );
}
